import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, PencilSimple, Trash, Lightning } from "@phosphor-icons/react";
import { COUNTIES } from "@/lib/data";

const EMP = ["Full-Time", "Part-Time", "PRN"];
const blank = {
  title: "", category: "Transportation", employment_types: ["Full-Time", "Part-Time"],
  counties: [], summary: "", responsibilities: "", qualifications: "", clearances: "",
  license_required: false, experience: "", schedule: "", compensation: "",
  urgent: false, status: "Open", order: 0,
};

export default function JobOpenings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/job-openings"); setRows(data); }
    catch { toast.error("Failed to load job openings"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const toggleArr = (k, v) => setEditing((e) => {
    const arr = e[k] || [];
    return { ...e, [k]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] };
  });

  const save = async () => {
    if (!editing.title?.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = { ...editing, order: Number(editing.order) || 0 };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      if (editing.id) await api.put(`/admin/job-openings/${editing.id}`, payload);
      else await api.post("/admin/job-openings", payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch { toast.error("Could not save"); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this job opening permanently?")) return;
    try { await api.delete(`/admin/job-openings/${id}`); setRows((r) => r.filter((x) => x.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-job-openings">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-slate-900">Job Openings</h1>
        <Button data-testid="jo-new" onClick={() => setEditing({ ...blank, counties: [...COUNTIES] })}
          className="h-11 rounded-xl bg-brand-purple font-semibold text-white hover:bg-brand-purple2">
          <Plus size={18} className="mr-1.5" /> New Opening
        </Button>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 font-semibold">Title</th>
                <th className="px-4 py-3 font-semibold">Category</th>
                <th className="px-4 py-3 font-semibold">Types</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No job openings yet.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50" data-testid={`jo-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-800">
                    {r.title}{r.urgent && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-600"><Lightning size={11} weight="fill" /> Urgent</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{r.category === "ODP" ? "ODP" : "Transportation"}</td>
                  <td className="px-4 py-3 text-slate-600">{(r.employment_types || []).join(", ")}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${r.status === "Open" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setEditing(r)} data-testid={`jo-edit-${r.id}`} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-brand-blue"><PencilSimple size={18} /></button>
                      <button onClick={() => remove(r.id)} data-testid={`jo-delete-${r.id}`} className="rounded-lg p-2 text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash size={18} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit Job Opening" : "New Job Opening"}</DialogTitle>
            <DialogDescription>Only list positions and duties CareDeserved is authorized to provide. Leave compensation blank to hide it.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5"><Label>Job Title</Label><Input data-testid="jo-title" value={editing.title} onChange={(e) => set("title", e.target.value)} /></div>
                <div className="space-y-1.5">
                  <Label>Category</Label>
                  <Select value={editing.category} onValueChange={(v) => set("category", v)}>
                    <SelectTrigger data-testid="jo-category"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Transportation">Transportation / NEMT</SelectItem><SelectItem value="ODP">ODP / Human Services</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Employment Types</Label>
                <div className="mt-2 flex flex-wrap gap-4">
                  {EMP.map((t) => (
                    <label key={t} className="flex cursor-pointer items-center gap-2">
                      <input type="checkbox" data-testid={`jo-emp-${t}`} checked={(editing.employment_types || []).includes(t)} onChange={() => toggleArr("employment_types", t)} className="h-4 w-4 rounded border-slate-300 accent-brand-purple" />
                      <span className="text-sm text-slate-700">{t}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label>Hiring Counties</Label>
                <div className="mt-2 flex flex-wrap gap-3">
                  <label className="flex cursor-pointer items-center gap-2">
                    <input type="checkbox" data-testid="jo-county-statewide" checked={(editing.counties || []).includes("Statewide")} onChange={() => toggleArr("counties", "Statewide")} className="h-4 w-4 rounded border-slate-300 accent-brand-purple" />
                    <span className="text-sm font-medium text-slate-700">Statewide</span>
                  </label>
                  {COUNTIES.map((c) => (
                    <label key={c} className="flex cursor-pointer items-center gap-2">
                      <input type="checkbox" checked={(editing.counties || []).includes(c)} onChange={() => toggleArr("counties", c)} className="h-4 w-4 rounded border-slate-300 accent-brand-purple" />
                      <span className="text-sm text-slate-700">{c}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5"><Label>Position Summary</Label><Textarea data-testid="jo-summary" className="min-h-[70px]" value={editing.summary} onChange={(e) => set("summary", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Responsibilities (one per line)</Label><Textarea data-testid="jo-responsibilities" className="min-h-[100px]" value={editing.responsibilities} onChange={(e) => set("responsibilities", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Minimum Qualifications (one per line)</Label><Textarea data-testid="jo-qualifications" className="min-h-[90px]" value={editing.qualifications} onChange={(e) => set("qualifications", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Required Clearances / Credentials (one per line)</Label><Textarea data-testid="jo-clearances" className="min-h-[80px]" value={editing.clearances} onChange={(e) => set("clearances", e.target.value)} /></div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5"><Label>Experience Requirements</Label><Input data-testid="jo-experience" value={editing.experience} onChange={(e) => set("experience", e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Schedule / Availability</Label><Input data-testid="jo-schedule" value={editing.schedule} onChange={(e) => set("schedule", e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Compensation (optional)</Label><Input data-testid="jo-compensation" placeholder="Leave blank to hide" value={editing.compensation} onChange={(e) => set("compensation", e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Display Order</Label><Input data-testid="jo-order" type="number" value={editing.order} onChange={(e) => set("order", e.target.value)} /></div>
              </div>

              <div className="flex flex-wrap items-center gap-6">
                <label className="flex cursor-pointer items-center gap-2"><input type="checkbox" data-testid="jo-license" checked={!!editing.license_required} onChange={(e) => set("license_required", e.target.checked)} className="h-4 w-4 rounded border-slate-300 accent-brand-purple" /><span className="text-sm text-slate-700">Driver's license required</span></label>
                <label className="flex cursor-pointer items-center gap-2"><input type="checkbox" data-testid="jo-urgent" checked={!!editing.urgent} onChange={(e) => set("urgent", e.target.checked)} className="h-4 w-4 rounded border-slate-300 accent-brand-purple" /><span className="text-sm text-slate-700">Urgently Hiring</span></label>
                <div className="flex items-center gap-2">
                  <Label className="mb-0">Status</Label>
                  <Select value={editing.status} onValueChange={(v) => set("status", v)}>
                    <SelectTrigger data-testid="jo-status" className="h-9 w-32"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Open">Open</SelectItem><SelectItem value="Closed">Closed</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>

              <Button onClick={save} disabled={saving} data-testid="jo-save" className="w-full rounded-full bg-brand-purple text-white hover:bg-brand-purple2">
                {saving ? "Saving…" : "Save Job Opening"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
